/**
 * Bundle Builder JavaScript
 * Handles quantity selection, progress bar updates, and cart addition
 */

class BundleBuilder {
  constructor(container, basePrice) {
    this.container = container;
    this.basePrice = basePrice;

    // Tier discount percentages from metafields
    this.tierDiscounts = {
      1: 0,
      2: parseFloat(container.getAttribute("data-tier-2-discount")) || 0,
      3: parseFloat(container.getAttribute("data-tier-3-discount")) || 0,
      4: parseFloat(container.getAttribute("data-tier-4-discount")) || 0,
    };

    // DOM Elements
    this.progressFill = container.querySelector("[data-progress-fill]");

    // Bundle summary elements (now in buy-buttons, outside container)
    this.totalBottlesEl = document.querySelector("[data-total-bottles]");
    this.subtotalEl = document.querySelector("[data-subtotal]");

    this.tierOptions = container.querySelectorAll("[data-tier]");
    this.variantItems = container.querySelectorAll("[data-variant-item]");

    // Tier price elements on progress bar
    this.tierPriceElements = container.querySelectorAll(".tier-price");

    // Store original tier prices for scaling with subscription
    this.originalTierPrices = [];
    this.tierPriceElements.forEach((el) => {
      const priceText = el.textContent.trim();
      const priceMatch = priceText.match(/[\d.,]+/);
      if (priceMatch) {
        const price = parseFloat(priceMatch[0].replace(",", ""));
        // Round to 2 decimal places to avoid floating point precision issues
        this.originalTierPrices.push(parseFloat(price.toFixed(2)));
      } else {
        this.originalTierPrices.push(parseFloat(this.basePrice.toFixed(2)));
      }
    });

    // Bundle button (now in buy-buttons, outside container)
    this.addButton = document.querySelector("[data-bundle-add]");
    this.buttonText = document.querySelector("[data-button-text]");
    this.buttonLoader = document.querySelector(".button-loader");

    this.init();
  }

  init() {
    // Setup quantity change handlers for each variant
    this.variantItems.forEach((item) => {
      this.setupQuantityHandlers(item);
    });

    // Setup bundle add to cart button
    if (this.addButton) {
      this.addButton.addEventListener("click", () => {
        this.addToCart();
      });
    }

    // Listen for custom subscription widget changes
    this.setupPurchaseOptionListeners();

    // Also listen for subscription-changed event from custom widget
    document.addEventListener("subscription-changed", () => {
      this.updateTierPrices();
      this.updateBundle();
    });

    // Wait for custom widget to initialize, then setup listeners
    setTimeout(() => {
      this.setupPurchaseOptionListeners();
    }, 500);
  }

  setupPurchaseOptionListeners() {
    // Avoid duplicate listeners
    if (this._listenersSet) return;

    // Find all subscription radio inputs from custom widget
    const inputs = document.querySelectorAll(
      'input[type="radio"][name="selling_plan"]',
    );

    if (inputs.length === 0) {
      return;
    }

    inputs.forEach((input) => {
      input.addEventListener("change", () => {
        this.updateTierPrices();
        this.updateBundle();
      });
    });

    this._listenersSet = true;
  }

  getCurrentPrice() {
    // Try to find checked radio from custom subscription widget
    const checkedRadio = document.querySelector(
      'input[type="radio"][name="selling_plan"]:checked',
    );

    // If no subscription widget found, use base price
    if (!checkedRadio) {
      return this.basePrice;
    }

    // Get price from data-variant-price attribute
    const variantPrice = checkedRadio.dataset.variantPrice;

    if (variantPrice) {
      // Extract numeric value from format like "$26.96 AUD" or "$29.95 AUD"
      const priceMatch = variantPrice.match(/[\d.,]+/);
      if (priceMatch) {
        const price = parseFloat(priceMatch[0].replace(",", ""));
        return price;
      }
    }

    // Fallback to base price if unable to extract from data attribute
    return this.basePrice;
  }

  getTierDiscount(totalQty) {
    if (totalQty >= 4) return this.tierDiscounts[4];
    if (totalQty === 3) return this.tierDiscounts[3];
    if (totalQty === 2) return this.tierDiscounts[2];
    return this.tierDiscounts[1];
  }

  getTierPrice(currentPrice, totalQty) {
    const discountPercent = this.getTierDiscount(totalQty);
    const discountAmount = currentPrice * (discountPercent / 100);
    return currentPrice - discountAmount;
  }

  hasAnyTierDiscount() {
    return (
      this.tierDiscounts[2] > 0 ||
      this.tierDiscounts[3] > 0 ||
      this.tierDiscounts[4] > 0
    );
  }

  updateTierPrices() {
    // Get current price to see if subscription is selected
    const currentPrice = this.getCurrentPrice();
    const ratio = currentPrice / this.basePrice;

    // Scale all tier prices by the same ratio if subscription is selected
    this.tierPriceElements.forEach((el, index) => {
      if (this.originalTierPrices[index]) {
        // Scale the original tier price by subscription ratio
        const scaledPrice = this.originalTierPrices[index] * ratio;
        el.textContent = `$${scaledPrice.toFixed(2)}/ea`;
      }
    });
  }

  setupQuantityHandlers(item) {
    const input = item.querySelector("[data-qty-input]");
    const minusBtn = item.querySelector("[data-qty-minus]");
    const plusBtn = item.querySelector("[data-qty-plus]");
    const addBtn = item.querySelector("[data-variant-add]");
    const qtyWrapper = item.querySelector(".quantity-wrapper");
    const maxQty = parseInt(input.dataset.max) || 999;

    // Add button - show quantity wrapper and set qty to 1
    addBtn.addEventListener("click", () => {
      input.value = 1;
      addBtn.style.display = "none";
      qtyWrapper.style.display = "inline-flex";
      this.updateBundle();
    });

    // Plus button
    plusBtn.addEventListener("click", () => {
      const currentVal = parseInt(input.value) || 0;
      if (currentVal < maxQty) {
        input.value = currentVal + 1;
        this.updateBundle();
      }
    });

    // Minus button
    minusBtn.addEventListener("click", () => {
      const currentVal = parseInt(input.value) || 0;
      if (currentVal > 1) {
        input.value = currentVal - 1;
        this.updateBundle();
      } else if (currentVal === 1) {
        // Hide quantity wrapper and show Add button
        input.value = 0;
        qtyWrapper.style.display = "none";
        addBtn.style.display = "inline-block";
        this.updateBundle();
      }
    });

    // Input change
    input.addEventListener("change", () => {
      let val = parseInt(input.value) || 0;
      if (val < 0) val = 0;
      if (val > maxQty) val = maxQty;

      if (val === 0) {
        qtyWrapper.style.display = "none";
        addBtn.style.display = "inline-block";
      } else {
        qtyWrapper.style.display = "inline-flex";
        addBtn.style.display = "none";
      }

      input.value = val;
      this.updateBundle();
    });
  }

  updateBundle(skipWidgetPriceUpdate = false) {
    const items = [];
    let totalBottles = 0;
    let subtotal = 0;

    // Get current price based on subscription/one-time selection
    const currentPrice = this.getCurrentPrice();

    // Collect items with quantities
    this.variantItems.forEach((item) => {
      const input = item.querySelector("[data-qty-input]");
      const qty = parseInt(input.value) || 0;
      const variantId = item.dataset.variantId;

      if (qty > 0) {
        items.push({ id: parseInt(variantId), quantity: qty });
        totalBottles += qty;
        item.classList.add("has-quantity");
      } else {
        item.classList.remove("has-quantity");
      }
    });

    // Calculate tier price — if no tier discounts configured, use current price directly
    const baseTierPrice = this.hasAnyTierDiscount()
      ? this.getTierPrice(this.basePrice, totalBottles)
      : this.basePrice;

    const tierPrice = this.hasAnyTierDiscount()
      ? this.getTierPrice(currentPrice, totalBottles)
      : currentPrice;

    subtotal = totalBottles * tierPrice;

    // Update UI elements
    if (this.totalBottlesEl) {
      this.totalBottlesEl.textContent = totalBottles;
    }
    if (this.subtotalEl) {
      this.subtotalEl.textContent = "$" + subtotal.toFixed(2);
    }

    // Update button text and state
    if (this.addButton && this.buttonText) {
      if (totalBottles === 0) {
        this.addButton.disabled = true;
        this.buttonText.textContent = "Select at least 1 bottle";
      } else {
        this.addButton.disabled = false;
        this.buttonText.textContent = "Add to cart • $" + subtotal.toFixed(2);
      }
    }

    // Update progress bar and tier highlights
    this.updateProgressBar(totalBottles);

    // Update custom subscription widget prices if available
    // Skip update when called from subscription option change
    // Pass base tier price so widget can apply its own subscription discount
    if (window.subscriptionWidget && !skipWidgetPriceUpdate) {
      if (totalBottles > 0) {
        window.subscriptionWidget.updatePrices(baseTierPrice, this.basePrice);
      } else {
        // Reset to original prices when no items selected
        window.subscriptionWidget.updatePrices(this.basePrice, this.basePrice);
      }
    }

    // Store items for cart addition
    this.container.dataset.bundleItems = JSON.stringify(items);
  }

  updateProgressBar(totalBottles) {
    let progress = 0;
    let activeTierCount = 0;

    // Remove active class from all tiers
    this.tierOptions.forEach((tier) => tier.classList.remove("active"));

    // Calculate progress and set active tiers
    if (totalBottles === 0) {
      progress = 0;
      activeTierCount = 0;
    } else if (totalBottles === 1) {
      progress = 0; // At first point
      activeTierCount = 1;
    } else if (totalBottles === 2) {
      progress = 33.33; // To second point
      activeTierCount = 2;
    } else if (totalBottles === 3) {
      progress = 66.66; // To third point
      activeTierCount = 3;
    } else {
      progress = 100; // To fourth point (complete)
      activeTierCount = 4;
    }

    // Add active class to all tiers up to current level
    for (let i = 0; i < activeTierCount; i++) {
      this.tierOptions[i].classList.add("active");
    }

    // Animate progress fill
    this.progressFill.style.width = progress + "%";
  }

  resetBundle() {
    // Reset all variant quantities
    this.variantItems.forEach((item) => {
      const input = item.querySelector("[data-qty-input]");
      const addBtn = item.querySelector("[data-variant-add]");
      const qtyWrapper = item.querySelector(".quantity-wrapper");

      input.value = 0;
      qtyWrapper.style.display = "none";
      addBtn.style.display = "inline-block";
      item.classList.remove("has-quantity");
    });

    // Update UI
    this.updateBundle();
  }

  async addToCart() {
    const itemsData = this.container.dataset.bundleItems;
    if (!itemsData || !this.addButton) return;

    let items = JSON.parse(itemsData);
    if (items.length === 0) return;

    // Check if subscription is selected from custom widget
    const subscriptionRadio = document.querySelector(
      'input[name="selling_plan"]:checked',
    );
    let sellingPlanId = null;

    if (subscriptionRadio && subscriptionRadio.value) {
      // Check if this is a subscription (not one-time purchase)
      const radioType = subscriptionRadio.dataset.radioType;

      if (radioType === "selling_plan") {
        // Get selling plan ID from value or data attribute
        sellingPlanId =
          subscriptionRadio.value || subscriptionRadio.dataset.sellingPlanId;
      }
    }

    // Add selling_plan to each item if subscription is selected
    if (sellingPlanId) {
      items = items.map((item) => ({
        ...item,
        selling_plan: parseInt(sellingPlanId) || sellingPlanId,
      }));
    }

    // Show loading state
    const originalText = this.buttonText.textContent;
    this.addButton.disabled = true;
    this.addButton.classList.add("loading");
    this.buttonText.textContent = "Adding...";
    if (this.buttonLoader) {
      this.buttonLoader.style.display = "flex";
    }

    let success = false;

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items }),
      });

      if (response.status === 429) {
        alert("Too many requests. Please wait a moment and try again.");
        return;
      }

      if (response.ok) {
        const data = await response.json();
        success = true;

        // Reset bundle builder
        this.resetBundle();

        // Fetch updated cart data and update theme
        try {
          const cartResponse = await fetch("/cart.js");
          const cart = await cartResponse.json();

          // Trigger theme's cart update event
          if (window.theme && window.theme.pubsub) {
            window.theme.pubsub.publish(
              window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
              {
                source: "bundle-builder",
                cart: cart,
              },
            );
          }

          // Dispatch ajaxProduct:added event to trigger cart drawer
          // This allows theme or cart apps (like Upcart) to handle cart opening properly
          document.dispatchEvent(
            new CustomEvent("ajaxProduct:added", {
              detail: {
                product: data,
              },
            }),
          );
        } catch (error) {
          // Don't alert - cart was added successfully
        }
      } else {
        const error = await response.json();
        alert(
          error.description || error.message || "Error adding items to cart",
        );
      }
    } catch (error) {
      alert("Error adding items to cart. Please try again.");
    } finally {
      // Hide loading state
      this.addButton.classList.remove("loading");
      if (this.buttonLoader) {
        this.buttonLoader.style.display = "none";
      }

      // Only restore button state if not successful
      // If successful, resetBundle() already handled the button state
      if (!success) {
        this.addButton.disabled = false;
        this.buttonText.textContent = originalText;
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  const bundleBuilderEl = document.querySelector("[data-bundle-builder]");
  if (!bundleBuilderEl) return;

  // Get base price from data attribute
  const basePrice = parseFloat(bundleBuilderEl.dataset.basePrice) || 0;

  // Initialize bundle builder and expose globally
  window.bundleBuilderInstance = new BundleBuilder(bundleBuilderEl, basePrice);
});
